import { makeZip } from './lib/zip';
import { isChoice, isPhoto, state } from './state';
import type { AnswerPrompt } from './types';

const LABEL_BASE_URL = '/images/label-layout-base.png';
const LABEL_TRIM_URL = '/images/label-layout-trim.png';
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

export class ExportValidationError extends Error {
  issues: string[];
  constructor(issues: string[]) {
    super(issues.join('\n'));
    this.name = 'ExportValidationError';
    this.issues = issues;
  }
}

export interface ExportResult {
  blob: Blob;
  filename: string;
}

interface PngInfo {
  bytes: Uint8Array;
  width: number;
  height: number;
}

export async function buildConsumerExport(): Promise<ExportResult> {
  const issues: string[] = [];
  validateState(issues);
  if (issues.length > 0) throw new ExportValidationError(issues);

  const base = state.baseImage!;
  const baseBytes = dataUrlToBytes(base.dataUrl);
  const baseDims = readPngDims(baseBytes);
  if (!baseDims) {
    throw new ExportValidationError([
      'Base image is not a PNG. Load a PNG before exporting (path will be /generation-base.png).',
    ]);
  }

  let labelBase: PngInfo;
  let labelTrim: PngInfo;
  try {
    [labelBase, labelTrim] = await Promise.all([
      fetchPng(LABEL_BASE_URL),
      fetchPng(LABEL_TRIM_URL),
    ]);
  } catch (err) {
    throw new ExportValidationError([(err as Error).message]);
  }

  const crop = state.cropArea!;
  const config = {
    baseImage: {
      path: '/generation-base.png',
      width: baseDims.w,
      height: baseDims.h,
    },
    outputCrop: { x: crop.x, y: crop.y, w: crop.w, h: crop.h },
    labelBase: {
      path: '/images/label-layout-base.png',
      width: labelBase.width,
      height: labelBase.height,
    },
    labelTrim: {
      path: '/images/label-layout-trim.png',
      width: labelTrim.width,
      height: labelTrim.height,
    },
    labelMode: 'label-trimmed',
    openingPrompt: state.openingPrompt,
    closingPrompt: state.closingPrompt,
    targetTemplate: state.targetTemplate,
    targets: state.targets.map((t) => ({
      id: t.id,
      name: t.name,
      priority: [...t.priority],
    })),
    areas: state.areas.map((a) => ({
      id: a.id,
      x: a.x,
      y: a.y,
      aspectRatio: a.aspectRatio,
      imageSize: a.imageSize,
      includeBase: a.includeBase,
      includePhoto: a.includePhoto,
      targets: a.targets.map((t) => ({
        id: t.id,
        targetId: t.targetId,
        description: t.description,
      })),
      extraImages: a.extraImages.map((ei) => ({
        id: ei.id,
        name: ei.name,
        dataUrl: ei.dataUrl,
        mimeType: ei.mimeType,
      })),
    })),
    questions: state.questions.map((q) => {
      if (isChoice(q)) {
        return {
          kind: 'choice' as const,
          id: q.id,
          heading: q.heading,
          hint: q.hint,
          title: q.title,
          promptLabel: q.promptLabel,
          colorIndex: q.colorIndex,
          maxSelections: q.maxSelections,
          options: q.options.map((o) => ({
            label: o.label,
            injects: o.injects,
            prompts: o.prompts.map(serializePrompt),
          })),
        };
      }
      return {
        kind: 'photo' as const,
        id: q.id,
        heading: q.heading,
        hint: q.hint,
        title: q.title,
        description: q.description,
        prompts: q.prompts.map(serializePrompt),
      };
    }),
  };

  const jsonBytes = new TextEncoder().encode(JSON.stringify(config, null, 2));
  const blob = makeZip([
    { path: 'shared/generationConfig.json', data: jsonBytes },
    { path: 'public/generation-base.png', data: baseBytes },
    { path: 'public/images/label-layout-base.png', data: labelBase.bytes },
    { path: 'public/images/label-layout-trim.png', data: labelTrim.bytes },
  ]);

  return { blob, filename: 'consumer-config.zip' };
}

export function downloadExport(result: ExportResult): void {
  const url = URL.createObjectURL(result.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function validateState(issues: string[]): void {
  if (!state.baseImage) {
    issues.push('No base image loaded.');
  }
  if (!state.cropArea) {
    issues.push('No output crop set.');
  }

  const targetIds = new Set(state.targets.map((t) => t.id));
  const targetNames = new Set(state.targets.map((t) => t.name));

  for (const area of state.areas) {
    for (const at of area.targets) {
      if (!targetIds.has(at.targetId)) {
        issues.push(
          `Area "${area.id}" target "${at.id}" references unknown targetId "${at.targetId}".`,
        );
      }
    }
  }

  for (const q of state.questions) {
    if (isChoice(q)) {
      q.options.forEach((o, i) => {
        checkAlternatives(o.prompts, `question "${q.id}" option ${i} ("${o.label}")`, targetNames, issues);
      });
    } else if (isPhoto(q)) {
      checkAlternatives(q.prompts, `question "${q.id}"`, targetNames, issues);
    }
  }

  if (!state.targetTemplate.includes('[DESCRIPTION]')) {
    issues.push('Target template is missing the [DESCRIPTION] token.');
  }
  if (!state.targetTemplate.includes('[UPDATE]')) {
    issues.push('Target template is missing the [UPDATE] token.');
  }

  const seen = new Set<string>();
  for (const q of state.questions) {
    const id = q.id?.trim() ?? '';
    if (!id) {
      issues.push('A question has an empty id.');
      continue;
    }
    if (seen.has(id)) {
      issues.push(`Duplicate question id: "${id}".`);
    }
    seen.add(id);
  }
}

function checkAlternatives(
  prompts: AnswerPrompt[],
  where: string,
  targetNames: Set<string>,
  issues: string[],
): void {
  for (const p of prompts) {
    for (const alt of p.alternatives) {
      if (!targetNames.has(alt.target)) {
        issues.push(`Prompt in ${where} references unknown target name "${alt.target}".`);
      }
    }
  }
}

function serializePrompt(p: AnswerPrompt): { id: string; alternatives: Array<{ target: string; text: string }> } {
  return {
    id: p.id,
    alternatives: p.alternatives.map((a) => ({ target: a.target, text: a.text })),
  };
}

async function fetchPng(url: string): Promise<PngInfo> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url} (HTTP ${res.status}).`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  const dims = readPngDims(bytes);
  if (!dims) throw new Error(`${url} is not a valid PNG.`);
  return { bytes, width: dims.w, height: dims.h };
}

function readPngDims(bytes: Uint8Array): { w: number; h: number } | null {
  if (bytes.length < 24) return null;
  for (let i = 0; i < 8; i++) if (bytes[i] !== PNG_SIGNATURE[i]) return null;
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { w: dv.getUint32(16, false), h: dv.getUint32(20, false) };
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) throw new Error('Invalid data URL.');
  const binary = atob(dataUrl.slice(comma + 1));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
